import { useEffect, useMemo, useState } from "react";

import { MinusCircleIcon } from "@heroicons/react/24/outline";
import { cva } from "class-variance-authority";
import moment from "moment";
import { useSearchParams } from "react-router-dom";
import { EmptyList } from "src/components/EmptyList";
import Loading from "src/components/Loading";
import SortIcon from "src/components/ui-icons/table/SortIcon";
import Pagination from "src/components/ui/table/Pagination";
import { TableHeaderColumnProps } from "src/components/ui/table/Table";
import { UserAvatarItem, UserAvatarItemStore } from "src/components/ui/TGUserAvatarsList";
import { usePagination } from "src/hooks/usePagination";
import { TableColumnWithIcon } from "src/pages/DashboardPage/acadia/mock";
import { ActionStatus } from "src/pages/DashboardPage/mytasks/meetingsPage/components/ActionStatus";
import { FlexCol, FlexRow } from "src/utils/flexLayouts";
import { shortNameFromEmail } from "src/utils/users.utils";
import { twMerge } from "tailwind-merge";
import { nameFromEmail } from "./../../../../utils/users.utils";
import { actionsStatus } from "src/api/config/systemSwitch";
import { siteUrl } from "src/api/config";

export type ActionReportTableHeadType =
  | "Meeting"
  | "Location"
  | "Department"
  | "ActionDescription"
  | "Creator"
  | "Responsible"
  | "CreatedDate"
  | "DueDate"
  | "Status"
  | "CloseDate";

const tableHeaderContent: Record<ActionReportTableHeadType, TableColumnWithIcon> = {
  Meeting: {
    title: "Название совещания",
    sort: true,
    style: { width: "20%" }, // Название совещания — самое широкое поле
  },
  Location: {
    title: "Локация",
    sort: false,
    style: { width: "15%" }, // Локация — среднее поле
  },
  Department: {
    title: "Отдел",
    sort: false,
    style: { width: "10%" }, // Отдел — чуть уже, чем Локация
  },
  ActionDescription: {
    title: "Описание действия",
    sort: true,
    style: { width: "20%" }, // Описание действия — среднее широкое
  },
  Creator: {
    title: "Создатель",
    sort: false,
    style: { width: "10%" }, // Создатель — узкое
  },
  Responsible: {
    title: "Ответственный",
    sort: false,
    style: { width: "10%" }, // Ответственный — узкое
  },
  CreatedDate: {
    title: "Дата создания",
    sort: true,
    style: { width: "10%" }, // Дата создания — узкое
  },
  DueDate: {
    title: "Срок выполнения",
    sort: true,
    style: { width: "10%" }, // Срок выполнения — узкое
  },
  Status: {
    title: "Статус",
    sort: false,
    style: { width: "10%" }, // Статус — узкое
  },
  CloseDate: {
    title: "Дата закрытия",
    sort: true,
    style: { width: "10%" }, // Дата закрытия — узкое
  },
};

function ActionsReportTable({
  list,
  loading,
}: // page,
  // changePage,
  {
    list: (Record<ActionReportTableHeadType | "ID" | "HashTag", any> & { ID: number })[];
    loading: boolean;
    // page: number;
    // changePage: (page: number) => void;
  }) {
  const [sortedField, setSortedField] = useState<keyof typeof tableHeaderContent | null>("Meeting");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc" | "default">("desc");
  const [page, setPage] = useState(1);
  const limit = 10;

  const handleSort = (field: keyof typeof tableHeaderContent) => {
    if (sortedField === field) {
      setSortDirection((prevDirection) =>
        prevDirection === "asc" ? "desc" : prevDirection === "desc" ? "default" : "asc"
      );
    } else {
      setSortedField(field);
      setSortDirection("asc");
    }
  };

  const sortedTableBodyContent = useMemo(() => {
    if (!sortedField || sortDirection === "default") {
      return list;
    }

    const sortedData = [...list];

    const [locales, options]: [Intl.LocalesArgument, Intl.CollatorOptions] = [["ru", "en"], { sensitivity: "base" }];

    sortedData.sort((a, b) => {
      const valueA = a[sortedField];
      const valueB = b[sortedField];

      if (sortedField === "CreatedDate" || sortedField === "DueDate") {
        const dateA = moment(a[sortedField], "DD.MM.YYYY");
        const dateB = moment(b[sortedField], "DD.MM.YYYY");

        if (!dateA.isValid() || !dateB.isValid()) {
          console.error("Неверная дата", a[sortedField], b[sortedField]);
          return 0;
        }

        return sortDirection === "asc"
          ? dateA.diff(dateB) // если dateA после dateB вернется положительное число
          : dateB.diff(dateA); // для обратного порядка сортировки
      }

      if (sortedField === "CloseDate") {
        // Если одно из значений `null` или `undefined`, оно автоматически идет в конец
        if (!a[sortedField] && !b[sortedField]) {
          return 0; // оба значения отсутствуют, равны
        }
        if (!a[sortedField]) {
          return 1; // a пустое, значит оно идет после
        }
        if (!b[sortedField]) {
          return -1; // b пустое, значит оно идет после
        }
        const dateA = moment(a[sortedField], "DD.MM.YYYY");
        const dateB = moment(b[sortedField], "DD.MM.YYYY");

        if (!dateA.isValid() || !dateB.isValid()) {
          console.error("Неверная дата", a[sortedField], b[sortedField]);
          return 0;
        }

        return sortDirection === "asc"
          ? dateA.diff(dateB) // если dateA после dateB вернется положительное число
          : dateB.diff(dateA); // для обратного порядка сортировки
      }

      if (sortedField === "Meeting") {
        return sortDirection === "asc"
          ? valueA.Title.localeCompare(valueB.Title, locales, options)
          : valueB.Title.localeCompare(valueA.Title, locales, options);
      }

      if (typeof valueA === "string" && typeof valueB === "string") {
        return sortDirection === "asc"
          ? valueA.localeCompare(valueB, locales, options)
          : valueB.localeCompare(valueA, locales, options);
      } else if (typeof valueA === "boolean" && typeof valueB === "boolean") {
        return sortDirection === "asc" ? Number(valueA) - Number(valueB) : Number(valueB) - Number(valueA);
      }

      return 0;
    });

    return sortedData;
  }, [sortedField, sortDirection, list]) as (Record<ActionReportTableHeadType, any> & { ID: number })[];

  const {
    numberOfPages,
    pageItems,
    length: activeMeetingsItemsCount,
  } = usePagination(sortedTableBodyContent, limit, page);

  function onChangePage(page: number) {
    setPage(page);
  }

  useEffect(() => {
    onChangePage(1);
  }, [list]);

  return (
    <FlexRow>
      <FlexCol className="w-full">
        <TableList
          loading={loading}
          head={tableHeaderContent}
          body={pageItems}
          sort={handleSort}
          sortDirection={sortDirection}
          sortedField={sortedField}
        />
        {activeMeetingsItemsCount > limit && (
          <Pagination numberOfPages={numberOfPages} page={page} onChange={onChangePage} />
        )}
      </FlexCol>
    </FlexRow>
  );
}

export default ActionsReportTable;

function getDateByStatus(item: Record<ActionReportTableHeadType, any> & { ID: number }) {
  if (
    (item.Status.ID.toString() === actionsStatus.cancel.toString() ||
      item.Status.ID.toString() === actionsStatus.closed.toString()) &&
    item.CloseDate
  ) {
    return moment(item.CloseDate).format("DD.MM.YYYY");
  } else {
    return "----";
  }
}

function TableList({
  head,
  body,
  sort,
  sortDirection,
  sortedField,
  loading,
}: {
  head: Record<keyof typeof tableHeaderContent, TableColumnWithIcon>;
  body: (Record<ActionReportTableHeadType, any> & { ID: number })[];
  sort: (f: keyof typeof tableHeaderContent) => void;
  sortedField: keyof typeof tableHeaderContent | null;
  sortDirection: "asc" | "desc" | "default";
  loading: boolean;
}) {
  const [query, setQuery] = useSearchParams();

  function openMeetingCard(id: number) {
    const newSearchParams = new URLSearchParams(query);

    newSearchParams.set("meetingCard", id.toString());

    setQuery(newSearchParams);
  }

  function openActionCard(id: number) {
    // const newSearchParams = new URLSearchParams(query);
    // newSearchParams.set("actionCard", id.toString());
    // setQuery(newSearchParams);
    window.open(`${siteUrl}/dashboard/3?actionCard=${id.toString()}`, "_blank");
  }

  return (
    <FlexRow className="px-6 py-5 overflow-x-auto bg-white rounded-lg table-custom shadow-custom scrollbar scrollbar-thumb-rounded-lg scrollbar-h-2 scrollbar-thumb-[#D1D5DB]">
      <FlexCol className="divide-y min-w-[2351px] grow">
        <FlexRow className="gap-x-4 pb-5">
          {Object.keys(head).map((fieldKey) => {
            const key = fieldKey as keyof typeof tableHeaderContent;

            return (
              <TableHeaderColumn
                key={fieldKey}
                tableHeaderContent={head[key]}
                sortDirection={sortedField === key ? sortDirection : "default"}
                onClick={() => sort(key)}
              />
            );
          })}
        </FlexRow>
        {!loading ? (
          Array.isArray(body) && body.length > 0 ? (
            body.map((item) => (
              <FlexRow
                className="py-4 hover:bg-[#F3F4F6] hover:bg-opacity-50 rounded-lg gap-x-4 -mx-2 px-2"
                key={item.ID}
              >
                {/* Название совещания */}
                <FlexCol
                  className="break-words cursor-pointer text-darkblue"
                  style={{ width: "20%" }}
                  onClick={() => openMeetingCard(item.Meeting.ID)}
                >
                  {item.Meeting.Title}
                </FlexCol>

                {/* Локация */}
                <FlexCol className="break-words" style={{ width: "15%" }}>
                  {item.Location}
                </FlexCol>

                {/* Отдел */}
                <FlexCol className="break-words" style={{ width: "10%" }}>
                  {item.Department}
                </FlexCol>

                {/* Описание действия */}
                <FlexCol
                  className="break-words cursor-pointer text-darkblue"
                  style={{ width: "20%" }}
                  onClick={() => openActionCard(item.ID)}
                >
                  {item.ActionDescription}
                </FlexCol>

                {/* Создатель */}
                <FlexCol className="break-words" style={{ width: "10%" }}>
                  <UserAvatarItemStore
                    item={{
                      name: nameFromEmail(item.Creator),
                      shortName: shortNameFromEmail(item.Creator),
                      email: item.Creator,
                      avatar: null,
                    }}
                    single={true}
                  />
                </FlexCol>

                {/* Ответственный */}
                <FlexCol className="break-words" style={{ width: "10%" }}>
                  <UserAvatarItemStore
                    item={{
                      name: nameFromEmail(item.Responsible),
                      shortName: shortNameFromEmail(item.Responsible),
                      email: item.Responsible,
                      avatar: null,
                    }}
                    single={true}
                  />
                </FlexCol>

                {/* Дата создания */}
                <FlexCol className="break-words" style={{ width: "10%" }}>
                  {moment(item.CreatedDate).format("DD.MM.YYYY")}
                </FlexCol>

                {/* Срок выполнения */}
                <FlexCol className="break-words" style={{ width: "10%" }}>
                  {moment(item.DueDate).format("DD.MM.YYYY")}
                </FlexCol>

                {/* Статус */}
                <FlexCol className="break-words" style={{ width: "10%" }}>
                  <ActionStatus title={item.Status.Title} color={item.Status.Color} />
                </FlexCol>

                {/* Дата закрытия */}
                <FlexCol className="break-words" style={{ width: "10%" }}>
                  {getDateByStatus(item)}
                </FlexCol>
              </FlexRow>
            ))
          ) : (
            <EmptyList icon={<MinusCircleIcon width={24} height={24} color="#637381" />} title={`Нет действий`} />
          )
        ) : (
          <div className="pt-4">
            <Loading />
          </div>
        )}
      </FlexCol>
    </FlexRow>
  );
}

export function TableHeaderColumn({ tableHeaderContent, sortDirection, onClick }: TableHeaderColumnProps) {
  return (
    <FlexCol className="select-none" style={tableHeaderContent.style}>
      <FlexRow className="gap-x-2 items-center">
        {tableHeaderContent.icon && <FlexCol>{tableHeaderContent.icon}</FlexCol>}
        <FlexCol className="font-medium text-black whitespace-pre-line w-fit">{tableHeaderContent.title}</FlexCol>
        {tableHeaderContent.sort && (
          <FlexCol className="w-auto cursor-pointer" onClick={onClick}>
            <SortIcon sortDirection={sortDirection} />
          </FlexCol>
        )}
      </FlexRow>
    </FlexCol>
  );
}

const criticalItemStyle = cva("block h-2 w-2 rounded-full", {
  variants: {
    active: {
      true: "bg-[#BA0C2F]",
      false: "bg-[#8899A8]",
    },
  },
  defaultVariants: {
    active: false,
  },
});

export function CriticallyListItem({ item }: { item: boolean }) {
  return (
    <FlexRow className="items-center space-x-1">
      <FlexCol>
        <span className={twMerge(criticalItemStyle({ active: item }))}></span>
      </FlexCol>
      <FlexCol>{item ? "" : "Не "}Критичный</FlexCol>
    </FlexRow>
  );
}
